import { IMenusModel } from "./AppMenus"
import { ICoordinates } from "./Common"

export interface IProjectLink {
    Type: "tel" | "email" | "link",
    Name: string,
    Url: string,
    Header: boolean,
    Footer: boolean
}

export interface IProjectSettings {
    IsComingSoon: boolean,
    IsPublished: boolean,
    Project:
    {
        Id: string,
        Title: string,
        TitleHTML: string,
        Subtitle: string,
        Domain: string,
        Keywords: string,
        Description: string,
        ShareImage: string,
        Folder?: string,
        Version: string,
        WalletEntityType?: EnumWalletEntityType, // entidade emissora do cartão/carteira do projeto (Payshop, SIBS, etc.)
    },
    Configuration:
    {
        Logos:
        {
            Main: string,
            Header: string,
            Login: string
        },
        DefaultImage?: string,
        Cards?: { // um projeto pode ter vários tipos/modelos de cartão físico
            Modules?: number[], // módulos (EnumWalletModules) a que este cartão se aplica
            Images?: { Front?: string, Back?: string },
            ProofDocuments?: { // documentos comprovativos exigidos para pedir este cartão
                Id?: string,
                Type?: number,
                Required?: boolean,
                Enabled?: boolean,
                Name?: string,
                ControlName: string,
                Description?: string
            }[]
        }[],
        Letter: {
            Type: number,
            Template: string, // imagem png
            AddressToSend?: { Street: string, ZipCode: string, Locality: string } // morada de envio da carta quando não é a do consumidor
        },
        PrintOptions: {
            EnabledValidationDate: boolean // mostra/imprime a data de validade no cartão
        },
        Documentation: { Name: string, Url: string, IsEnabled: boolean }[],
        MarketPlace: { // configuração do marketplace do projeto
            Enable: boolean,
            Compliance: { // requisito de compliance apresentado ao aderir ao marketplace
                Id: number,
                Type: number,
                Method: number,
                Name: string,
                Resource: string,
                Url: string,
                Description: string,
                ControlName: string,
                Enabled: boolean
            }
        }
    }
    GoogleMaps: {
        Key: string
    },
    SEO:
    {
        GoogleAnalytics: string,
        GoogleSiteVerification: string
    }
    Coordinates: ICoordinates,
    Menus: IMenusModel[],
    Links: IProjectLink[],
    Contacts: IProjectLink[],
    OpeningHours: string,
    MerchantsRequests:
    {
        StartDate: string,
        EndDate: string
    },
    CouponRegistration:
    {
        StartDate: string,
        EndDate: string
    }
    NotifyUserAlerts: boolean,
    Pages:
    {
        Home: IProjectSettingsPages,
        Drafts?: { // imagens de fundo usadas nos ecrãs de rascunho/pré-adesão
            Banner?: {
                Url: string
            },
            Background?: {
                Url: string
            }
        },
        Merchants?: {
            Source?: any, // origem/import dos comerciantes (config específica por projeto)
            ExternalManualProcess?: { // registo manual do estado do processo do perfil numa entidade externa, ex.: SIBS (ausente = funcionalidade desligada)
                Enabled?: boolean,
                Title?: string, // título do botão e do modal; omitido = "Processo manual SIBS"
                IdProject?: number // Id do projeto na entidade externa (ver EnumRequestsCardIdProject)
            }
        },
        Consumer?: {
            RequiredEmail?: boolean;
            Source?: any, // origem/import dos consumidores (config específica por projeto)
            ConsentIdProjects?: number[], // ids dos projetos cujos consentimentos são apresentados no pedido (ausente = todos)
            ConfigureModule?: {
                MaxEndDateYearsFromNow?: number // limite de anos a partir de hoje para a data de fim de um módulo
            }
        },
        ConsumerChargings?: { // ficheiros de importação para carregamento de consumidores (ex.: campanhas de apoio)
            Templates?: IConsumerChargingTemplate[]
        },
        Movements?: {
            ReadInvoiceMerchantMatch?: string[], // termos usados para casar a fatura lida (OCR/leitura) com o comerciante
            ConsumptionMinDays?: number, // janela mínima de dias permitida para registar um consumo
            ConsumptionMaxDays?: number, // janela máxima de dias permitida para registar um consumo
            IdProject?: EnumRequestsCardIdProject, // integração implementada: SIBS = 7, Payshop = 2 (ver EnumRequestsCardIdProject)
            PaymentRequestTimeoutMinutes?: number // SIBS: tempo (minutos) de espera do pagamento no TPA; null = não aplicável
        },
        RequestsCard?: { // pedido de cartão físico junto de uma entidade externa
            WalletEntityType?: EnumWalletEntityType,
            IdProject?: number // Id do projeto na entidade externa (ver EnumRequestsCardIdProject)
        }
    },
    ApplicationDocuments: IProjectApplicationDocuments[], // documentos exigidos na candidatura/adesão ao projeto
    EquipmentTypes?: IEquipmentTypeConfig[], // tipos de equipamento disponíveis para os comerciantes deste projeto
    ConsumptionAttachments?: IProjectApplicationDocuments[] // documentos comprovativos exigidos ao registar um consumo

}

export interface IProjectApplicationDocuments {
    Type: number,
    Name: string,
    Required: boolean,
    IsEnabled: boolean,
    Module?: number[], // módulos a que este documento se aplica; vazio/omisso = todos
    SubTitle?: string,
    Description?: string,
    MCC?: string[], // MCCs (categorias de comerciante) a que este documento se aplica
    MerchantNameMatch?: string[] // termos usados para casar o nome do comerciante na fatura com este documento
}

// entidade externa que emite/gere o cartão físico ou carteira do consumidor
export enum EnumWalletEntityType {
    None = 'none',
    Payshop = 'Payshop',
    SIBS = 'SIBS'
}

// Id do projeto junto da entidade externa de pedido de cartão (usado em RequestsCard.IdProject)
export enum EnumRequestsCardIdProject {
    Payshop = 2,
    SIBS = 7,
    MicroIO = 10
}

// Id do projeto/tipo de equipamento (usado em EquipmentTypes[].IdProject)
export enum EnumEquipmentTypes {
    Parking = 6, // Estacionamento (Soltráfego)
    SIBSTerminals = 7 // Terminais SIBS
}

// tipo de equipamento (ex.: terminal/leitor) disponibilizado aos comerciantes do projeto
export interface IEquipmentTypeConfig {
    IdProject: EnumEquipmentTypes;
    Name: string;
    Enabled: boolean;
    Description?: string;
}

// modelo de ficheiro de importação para carregamento de consumidores (ConsumerChargings.Templates)
export interface IConsumerChargingTemplate {
    Enable: boolean,
    Path: string, // caminho do ficheiro template a disponibilizar para download
    Type: number, // tipo de verificação do documento (ENUMTypeDocumentVerification)
    Name: string,
    Columns?: IConsumerChargingTemplateColumns
}

// índice (0-based) de cada coluna no ficheiro de carregamento, por variar consoante a entidade que o gera
export interface IConsumerChargingTemplateColumns {
    NIF: number,
    Value: number,
    Name: number,
    StartRow: number // linha (0-based) onde começam os dados, a seguir aos cabeçalhos
}

export interface IProjectSettingsPages {
    Header:
    {
        Title: string,
        Images: { Alt?: string, Url: string }[],
        Description: string
    },
    Sections:
    {
        Title: string,
        Side?: 'left' | 'right' | 'none',
        Background?: string,
        Color?: string,
        Image: { Alt?: string, Url: string },
        Description: string,
        Buttons?:
        {
            Label: string,
            Path: string,
            Icon?: string,
            Color?: string,
            External?: boolean
        }[]
    }[]
}
